import React, {useEffect, useRef, useState} from 'react';
import {Image, Layer, Stage} from "react-konva";
import BackgroundRectangleShape from "../../../design/components/design-editor/konva-editor/background-rectangle-shape";
import ProductImageRectangle from "../../../shopify/components/product-image/product-image-rectangle";
import _ from "lodash";
import ImageRectangle from './image-rectangle';
import DesignTransformer from "../../../design/components/design-editor/konva-editor/design-transformer";
import ImageTransformer from "./image-transformer";
import TextPathShape from "../../../design/components/design-editor/konva-editor/text-path-shape";
import TextShape from "../../../design/components/design-editor/konva-editor/text-shape";
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon
} from "@heroicons/react/24/solid";
import {STAGE_ZOOM_STEP} from "../../constants/media.constant";
import ImageShape from "../../../design/components/design-editor/konva-editor/image-shape";


function downloadURI(uri, name) {
    let link = document.createElement('a');
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const initialContainerSize = {width: 500, height: 500};

const RenderMediaStage = ({
                              settings,
                              setSettings,
                              dimensionsMap,
                              selectedImage,
                              setSelectedImage,
                              imageContainerClass = 'featured-image-stage-container',
                              containerWidth = '100px',
                              containerHeight = '100px',
                              setImageBase64 = null,
                              triggerImageDataPrep = false,
                              layers = [],
                              handleAttributeChanges,
                              imageMedia,
                              isViewOnly = false,
                              setCurrentStageRef = null,
                              setCurrentStageScale = null,
                          }) => {
    const [initialStep, setInitialStep] = useState(true);
    const stageRef = React.useRef(null);
    const layerRef = React.useRef(null);
    const backgroundRef = React.useRef(null);
    const [stagePos, setStagePos] = useState(null);
    const [stageScale, setStageScale] = useState(1);
    const [stageSize, setStageSize] = useState(null);
    const [showStage, setShowStage] = useState(false);
    const [showContainer, setShowContainer] = useState(true);
    const [containerSize, setContainerSize] = useState(null);
    const transformerRef = React.useRef(null);
    const [childItemsRefList, setChildItemsRefList] = useState([]);
    const [stageScalePercentage, setStageScalePercentage] = useState(100);

    useEffect(() => {
        if (initialStep) {
            setShowStage(false);
            setInitialStep(false);
            initStage();
        }
    }, [initialStep]);

    const initStage = () => {
        fitStageIntoParentContainer(settings);
        setShowStage(true);
        refreshStage();
    }

    useEffect(() => {
        if (selectedImage) {
            const selectedChildItemRef = childItemsRefList[selectedImage.id];
            handleClick({target: selectedChildItemRef}, selectedImage);
        } else {
            transformerRef?.current?.nodes([]);
        }
    }, [selectedImage]);

    const prepareContainerSize = (refresh = false, currentSettings?) => {
        const dimension = dimensionsMap[imageMedia['dimensionType']];
        const width = dimension?.width;
        const height = dimension?.height;

        if (!currentSettings) {
            currentSettings = {...settings};
        }

        const newContainerSize = {width: width * currentSettings.scale, height: height * currentSettings.scale};
        setContainerSize(newContainerSize);
        setShowContainer(true);

        currentSettings.width = newContainerSize?.width;
        currentSettings.height = newContainerSize?.height;
        setSettings(currentSettings);

        if (refresh) {
            setShowStage(false);
            fitStageIntoParentContainer(currentSettings);
            setShowStage(true);
            refreshStage();
        }
    }

    const refreshStage = () => {
        setTimeout(() => {
            const stage = stageRef.current;

            if (stage) {
                stage.draw();

                if (setImageBase64) {
                    prepareImageBase64Data();
                }

                if (setCurrentStageScale) {
                    setCurrentStageScale(stageScale);
                }

                if (setCurrentStageRef) {
                    setCurrentStageRef(stageRef);
                }
            }
        }, 10);
    }

    const fitStageIntoParentContainer = (currentSettings) => {
        const stage = stageRef.current;
        let container = document.querySelector('#stage-parent');
        // @ts-ignore
        let containerWidth = container?.offsetWidth;
        let scale = containerWidth / currentSettings?.width;

        setStageScale(scale);
        setStageScalePercentage(_.parseInt((scale * 100).toString()));

        stage.width(currentSettings?.width * scale);
        stage.height(currentSettings?.height * scale);
        stage.scale({x: scale, y: scale});

        setStageSize({width: stage?.width(), height: stage?.height()});
    }

    const afterImagePropsChanged = (productImage, newAttributes) => {
        productImage.props = {...productImage.props, ...newAttributes};
        refreshStage();
    }

    const handleExport = (scalePercentage) => {
        let pixelRatio = 100 / (stageScale * 100);
        pixelRatio = pixelRatio * (scalePercentage / 100);

        const uri = stageRef.current.toDataURL({
            pixelRatio: pixelRatio
        });

        let fileName = _.kebabCase(imageMedia?.name) + '-' + _.replace(_.join(_.split(imageMedia.size, ' '), ''), 'px', '') + '-' + scalePercentage + '.jpg';

        downloadURI(uri, fileName);
    };

    const prepareImageBase64Data = () => {
        if (setImageBase64) {
            setTimeout(() => {
                const pixelRatio = 100 / (stageScale * 100);
                const uri = stageRef.current.toDataURL({
                    pixelRatio: pixelRatio,
                });

                setImageBase64(uri);
            }, 2);
        }
    }

    const handleClick = (e, itemDetail) => {
        const stage = stageRef.current;
        // if click on empty area - remove all selections
        if (e.target === stage) {
            transformerRef.current.nodes([]);
            return;
        }

        if (transformerRef && transformerRef.current) {
            transformerRef.current.nodes([e.target]);
            transformerRef.current.getLayer().batchDraw();
        }
    }

    const zoomIn = () => {
        setShowContainer(false);
        let currentStageScale = stageScale;
        currentStageScale += STAGE_ZOOM_STEP;

        processStageScaleInSettings(currentStageScale);
    }

    const zoomOut = () => {
        setShowContainer(false);
        let currentStageScale = stageScale;
        currentStageScale -= STAGE_ZOOM_STEP;

        processStageScaleInSettings(currentStageScale);
    }

    const processStageScaleInSettings = (currentStageScale) => {
        const currentSettings = {...settings};

        currentSettings.scale = currentStageScale;
        const newWidth = currentSettings.width * currentStageScale;
        const newHeight = currentSettings.height * currentStageScale;

        currentSettings.stageContainer = {
            width: _.parseInt(newWidth.toFixed(0)),
            height: _.parseInt(newHeight.toFixed(0))
        };

        setSettings(currentSettings);

        setTimeout(() => {
            setShowContainer(true);
            fitStageIntoParentContainer(currentSettings);

            if (setCurrentStageScale) {
                setCurrentStageScale(currentStageScale);
            }

            if (setCurrentStageRef) {
                setCurrentStageRef(stageRef);
            }

        }, 1);
    }

    const changeStageScale = (scale) => {
        const stage = stageRef.current;

        stage.width(settings.width * scale);
        stage.height(settings.height * scale);
        stage.scale({x: scale, y: scale});

        stage.draw();
    }

    return (
        <>

            <div className='ml-4'>
                {!isViewOnly &&
                    <div className="w-full flex justify-between items-center">
                        <div className={'flex justify-start items-center'}>
                            <button onClick={zoomOut}
                                    className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500">
                                <div className="p-2">
                                    <MagnifyingGlassMinusIcon className="h-6 w-6"/>
                                </div>
                            </button>

                            <button
                                className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 py-2 px-3 ml-2">
                                <div className="font-bold text-gray-600">{stageScalePercentage} %</div>
                            </button>

                            <button onClick={zoomIn}
                                    className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">
                                <div className="p-2">
                                    <MagnifyingGlassPlusIcon className="h-6 w-6"/>
                                </div>
                            </button>

                            <button onClick={refreshStage}
                                    className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">
                                <div className="p-2">
                                    <ArrowPathIcon className="h-6 w-6"/>
                                </div>
                            </button>

                            <button
                                className="button-outline-green px-3 py-2 ml-2 my-1 flex items-center justify-center"
                                onClick={() => handleExport(100)}>
                                <div className="mr-2">
                                    <ArrowDownTrayIcon className="h-6 w-6"/>
                                </div>
                                JPG <span
                                className={'text-sm ml-2 py-0 px-2 bg-green-500 text-white rounded-full'}>100%</span>
                            </button>

                            <button
                                className="button-outline-green px-3 py-2 ml-2 my-1 flex items-center justify-center"
                                onClick={() => handleExport(75)}>
                                <div className="mr-2">
                                    <ArrowDownTrayIcon className="h-6 w-6"/>
                                </div>
                                JPG <span
                                className={'text-sm ml-2 py-0 px-2 bg-green-500 text-white rounded-full'}>75%</span>
                            </button>

                            <button
                                className="button-outline-green px-3 py-2 ml-2 my-1 flex items-center justify-center"
                                onClick={() => handleExport(50)}>
                                <div className="mr-2">
                                    <ArrowDownTrayIcon className="h-6 w-6"/>
                                </div>
                                JPG <span
                                className={'text-sm ml-2 py-0 px-2 bg-green-500 text-white rounded-full'}>50%</span>
                            </button>

                            <button
                                className="button-outline-green px-3 py-2 ml-2 my-1 flex items-center justify-center"
                                onClick={() => handleExport(25)}>
                                <div className="mr-2">
                                    <ArrowDownTrayIcon className="h-6 w-6"/>
                                </div>
                                JPG <span
                                className={'text-sm ml-2 py-0 px-2 bg-green-500 text-white rounded-full'}>25%</span>
                            </button>
                        </div>
                    </div>
                }

                {showContainer &&
                    <div id="stage-parent"
                         className={`mt-4`}
                         style={settings?.stageContainer}>

                        <Stage width={stageSize?.width} height={stageSize?.height}
                               style={{'backgroundColor': '#FFFFFF'}}
                               className="flex justify-start border border-gray-800 shadow-lg"
                               draggable={false}
                               onClick={(e) => {
                                   handleClick(e, null)
                               }}
                               listening={true}
                               perfectDrawEnabled={false}
                               ref={stageRef}
                               scaleX={stageScale}
                               scaleY={stageScale}
                               x={stagePos?.x}
                               y={stagePos?.y}>

                            {showStage &&
                                <>

                                    {layers?.map((layer, layerIndex) => (

                                        <Layer key={`layer-${layerIndex}`}
                                               visible={!layer?.isHidden}>

                                            {layer?.children?.map((childItem, childItemIndex) => (
                                                <React.Fragment key={childItemIndex}>
                                                    {childItem?.isBackground &&
                                                        <BackgroundRectangleShape
                                                            backgroundRef={backgroundRef}
                                                            background={childItem?.settings?.background}
                                                            stageWidth={settings?.width}
                                                            stageHeight={settings?.height}/>
                                                    }

                                                    {childItem?.isImage &&
                                                        <ImageRectangle
                                                            childItemsRefList={childItemsRefList}
                                                            onChange={(event) => {
                                                                afterImagePropsChanged(childItem, event);
                                                            }}
                                                            onClick={(e) => {
                                                                handleClick(e, childItem);
                                                            }}
                                                            setSelectedImage={setSelectedImage}
                                                            isSelected={selectedImage?.id === childItem?.id}
                                                            imageDetail={childItem}/>
                                                    }

                                                    {/*{childItem?.isImage &&
                                                        <ImageRectangle
                                                            childItemsRefList={childItemsRefList}
                                                            onChange={(event) => {
                                                                afterImagePropsChanged(childItem, event);
                                                            }}
                                                            onClick={(e) => {
                                                                handleClick(e, childItem);
                                                            }}
                                                            setSelectedImage={setSelectedImage}
                                                            isSelected={selectedImage?.id === childItem?.id}
                                                            imageDetail={childItem}/>
                                                    }*/}

                                                    {childItem?.isText &&

                                                        <>
                                                            {childItem?.radius ? <TextPathShape
                                                                key={childItem?.key}
                                                                shapeProps={childItem}
                                                                isSelected={childItem?.key === selectedImage?.key}
                                                                onSelect={undefined}
                                                                onChange={(newAttrs) => {
                                                                    handleAttributeChanges(newAttrs, layerIndex, childItemIndex);
                                                                }}
                                                                transformerRef={transformerRef}
                                                                handleGuidelineDragEnd={() => {
                                                                    //handleGuidelineDragEnd();
                                                                }}
                                                                handleGuidelineDragMove={() => {
                                                                    //handleGuidelineDragMove();
                                                                }}
                                                            /> : <TextShape
                                                                key={childItem?.key}
                                                                shapeProps={childItem}
                                                                isSelected={childItem?.key === selectedImage?.key}
                                                                onSelect={undefined}
                                                                onChange={(newAttrs) => {
                                                                    handleAttributeChanges(newAttrs, layerIndex, childItemIndex);
                                                                }}
                                                                stagePos={stagePos}
                                                                transformerRef={transformerRef}
                                                                handleGuidelineDragEnd={() => {
                                                                    //handleGuidelineDragEnd();
                                                                }}
                                                                handleGuidelineDragMove={() => {
                                                                    //handleGuidelineDragMove();
                                                                }}
                                                            />}
                                                        </>

                                                    }
                                                </React.Fragment>
                                            ))}

                                            {!isViewOnly &&
                                                <ImageTransformer
                                                    transformerRef={transformerRef}
                                                />
                                            }

                                        </Layer>

                                    ))}

                                </>
                            }

                        </Stage>
                    </div>
                }
            </div>

        </>
    )
}

export default RenderMediaStage;

